/**
 * Money helpers. Amounts are stored as decimal strings ("12.50", "15.833333333333334") and
 * computed with decimal.js — never with binary floating point.
 */
import Decimal from "decimal.js";

Decimal.set({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

export { Decimal };
export type DecimalString = string;

export class MoneyError extends Error {
  override readonly name = "MoneyError";
}

export const ZERO: DecimalString = "0";

export function dec(value: Decimal.Value): Decimal {
  return new Decimal(value);
}

export function sum(values: Iterable<Decimal.Value>): Decimal {
  let total = new Decimal(0);
  for (const v of values) total = total.plus(v);
  return total;
}

/** Canonical storage form: no exponent, no trailing zeros beyond what is needed. */
export function toDecimalString(value: Decimal.Value): DecimalString {
  const d = new Decimal(value);
  return d.isZero() ? "0" : d.toFixed();
}

export function round2(value: Decimal.Value): Decimal {
  return new Decimal(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

/**
 * Parses a user-typed euro amount. Accepts Italian ("1.234,56", "12,5") and plain ("1234.56")
 * notation. Rejects zero, negatives, more than 2 decimals, and anything that is not a number.
 */
export function parseAmountInput(raw: string): DecimalString {
  const text = raw.trim().replace(/\s|€/g, "");
  if (text === "") throw new MoneyError("Inserisci un importo.");
  let normalized: string;
  if (text.includes(",")) {
    // Italian: dots are thousand separators, the comma is the decimal separator.
    if (!/^\d{1,3}(\.\d{3})*(,\d+)?$|^\d+(,\d+)?$/.test(text)) throw new MoneyError("Importo non valido.");
    normalized = text.replace(/\./g, "").replace(",", ".");
  } else {
    if (!/^\d+(\.\d+)?$/.test(text)) throw new MoneyError("Importo non valido.");
    normalized = text;
  }
  const value = new Decimal(normalized);
  if (value.decimalPlaces() > 2) throw new MoneyError("Usa al massimo due decimali.");
  if (value.lte(0)) throw new MoneyError("L'importo deve essere maggiore di zero.");
  return value.toFixed(2);
}

/** True when a stored amount has at most two decimals (every amount entered in the app). */
export function hasAtMostTwoDecimals(value: DecimalString): boolean {
  return new Decimal(value).decimalPlaces() <= 2;
}

/** Equality within a tolerance, for reconciliation with Excel's IEEE doubles. */
export function approxEqual(a: Decimal.Value, b: Decimal.Value, tolerance: Decimal.Value = "0.005"): boolean {
  return new Decimal(a).minus(b).abs().lte(tolerance);
}
